import type { Participant } from 'shared';
import { useDeleteParticipant } from '@/hooks/useParticipants';
import { ConfirmDialog } from '@/components/activities/ActivityUi';

interface DeleteParticipantModalProps {
  participant: Pick<Participant, 'id' | 'nome' | 'apelido'>;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Deleting a participant anonymises them (RGPD): their personal data is erased
 * for good, and what they did stays in the figures without a name.
 */
export default function DeleteParticipantModal({ participant, onClose, onDeleted }: DeleteParticipantModalProps) {
  const del = useDeleteParticipant();
  const name = `${participant.nome} ${participant.apelido}`;

  const confirm = () => {
    del.mutate(participant.id, {
      onSuccess: () => {
        onDeleted();
        onClose();
      },
    });
  };

  return (
    <ConfirmDialog
      title="Eliminar participante"
      confirmLabel="Eliminar de vez"
      isPending={del.isPending}
      onConfirm={confirm}
      onClose={onClose}
    >
      <div className="space-y-2">
        <p>
          Os dados pessoais de <span className="font-medium text-foreground">{name}</span> são
          apagados de vez: nome, contactos, CC, morada, escola e os dados do Encarregado de
          Educação. Os Encarregados associados deixam de ter acesso.
        </p>
        <p>
          Ficam, sem nome, as visitas, sessões, badges e o ano de nascimento, para as estatísticas.
          Se a pessoa pediu os seus dados, exporta-os antes. Esta ação não pode ser desfeita.
        </p>
        {del.isError && (
          <p className="text-destructive" role="alert">
            {del.error?.message ?? 'Erro ao eliminar.'}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
